import { Transition } from '@headlessui/react';
import { memo, useMemo, useRef, useSyncExternalStore } from 'react';
import { SelectionElementType } from '../Outlines/types';
import PlayerMenuTooltip from '../PlayerMenus/PlayerMenuTooltip';
import SpeakerLabelColorsMenu from './SpeakerLabelColorsMenu';
import SpeakerLabelBorderRadiusToggle from './SpeakerLabelBorderRadiusToggle';
import SpeakerLabelRemoveButton from './SpeakerLabelRemoveButton';
import SpeakerLabelEditButton from './SpeakerLabelEditButton';
import SpeakerLabelTypographyMenu from './SpeakerLabelTypographyMenu';
import SpeakerLabelAppearanceMenu from './SpeakerLabelAppearanceMenu';
import SpeakerLabelAnimationMenu from './SpeakerLabelAnimationMenu/SpeakerLabelAnimationMenu';
import { classnames } from '@/libs/utils';
import FontSelector from '@/Pages/Clip/SideBar/FontSelector/FontSelector';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { isClipAudiogram } from '@/libs/clipUtils';

function SpeakerLabelMenu({ speakerId }: { speakerId: string }) {
  const menuRef = useRef<HTMLDivElement>(null);
  const { clipData } = useClipsContext();
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);

  const isAudiogram = isClipAudiogram(clipData);

  const useAnimatedSpeakerCards = !isAudiogram && featureFlags[FeatureFlagKeys.Use_CL_Animated_Speaker_Cards];

  const isPortrait = useMemo(() => {
    return clipData.asset_metadata.size === 'PORTRAIT';
  }, [clipData]);

  return (
    <Transition
      appear
      as="div"
      show
      className="z-[60] flex cursor-auto items-center justify-center transition-all duration-100"
      enterFrom="opacity-0 max-h-0"
      enterTo="opacity-100 max-h-96"
      leaveFrom="opacity-100 max-h-96"
      leaveTo="opacity-0 max-h-0"
    >
      <div
        id="speaker-label-menu"
        className="relative flex space-x-1 rounded-lg border bg-white p-0.5 shadow"
        onPointerDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
        ref={menuRef}
      >
        {useAnimatedSpeakerCards ? (
          <>
            <div className="relative z-10">
              <SpeakerLabelTypographyMenu clip={clipData} speakerId={speakerId} />
            </div>
            <div className="relative z-10">
              <SpeakerLabelAppearanceMenu clip={clipData} />
            </div>
            {!isAudiogram && (
              <div className="relative z-10">
                <SpeakerLabelAnimationMenu clip={clipData} />
              </div>
            )}
            <div className="relative z-10">
              <SpeakerLabelEditButton speakerId={speakerId} />
            </div>
            {!isAudiogram && (
              <div className="relative z-10">
                <SpeakerLabelRemoveButton />
              </div>
            )}
          </>
        ) : (
          <>
            <div
              className={classnames(
                'group/button relative cursor-pointer rounded-md text-sm',
                isPortrait ? 'w-28' : 'w-32'
              )}
            >
              <FontSelector isInline={true} fontTarget={SelectionElementType.SPEAKER_LABEL} />
              <div className="flex -translate-y-5 items-center justify-center">
                <PlayerMenuTooltip content="Font" />
              </div>
            </div>
            <div className="relative z-10">
              <SpeakerLabelColorsMenu clip={clipData} />
            </div>
            {!isAudiogram && (
              <div className="relative z-10">
                <SpeakerLabelBorderRadiusToggle />
              </div>
            )}
            <div className="relative z-10">
              <SpeakerLabelEditButton speakerId={speakerId} />
            </div>
            {!isAudiogram && (
              <div className="relative z-10">
                <SpeakerLabelRemoveButton />
              </div>
            )}
          </>
        )}
      </div>
    </Transition>
  );
}

export default memo(SpeakerLabelMenu);
